import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { toast } from 'react-toastify';
import api from '../../services/api';
import apiErrorFormatter from '../../services/apiErrorFormatter';

import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

function DialogAcceptMaintenanceTask(props) {
  const { t } = useTranslation('maintenance');

  const { open, onClose, onValidate, maintenanceTask } = props;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const [comment, setComment] = useState('');

  const validate = async () => {
    setLoading(true);
    setErrors([]);
    await api
      .put('/maintenance/accept-maintenance-task', {
        maintenanceTaskId: maintenanceTask?.id,
        comment: comment,
      })
      .then((res) => {
        toast.success(t('maintenance_task_accepted'));
        onValidate();
      })
      .catch((err) => {
        setErrors(apiErrorFormatter(err));
      });
    setLoading(false);
  };

  const dialogExited = () => {
    setLoading(false);
    setErrors([]);
    setComment('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      fullWidth
      TransitionProps={{
        onExited: dialogExited,
      }}
    >
      <DialogTitle id="dialog-title">
        {t('accept_maintenance_task')}
      </DialogTitle>
      <DialogContent id="dialog-description">
        <Box mt={1}>
          <TextField
            fullWidth
            multiline
            id="comment"
            label={t('common:comment')}
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);
            }}
            disabled={loading}
          />
        </Box>
        {errors.length > 0 && (
          <Box>
            {errors
              .slice()
              .reverse()
              .map((error) => (
                <Box key={error} mt={1}>
                  <Alert variant="outlined" severity="error">
                    {t(error)}
                  </Alert>
                </Box>
              ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{ mb: 2, mx: 2, display: 'flex', justifyContent: 'space-between' }}
      >
        <Button
          variant="contained"
          sx={{
            width: '40%',
            height: '90px',
            color: 'primary.contrastText',
            backgroundColor: 'success.main',
            ':hover': {
              color: 'primary.contrastText',
              backgroundColor: 'success.dark',
            },
          }}
          onClick={validate}
          disabled={loading}
        >
          {!loading ? (
            t('accept')
          ) : (
            <CircularProgress color="inherit" size={20} />
          )}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          sx={{ width: '40%', height: '90px' }}
          onClick={onClose}
          disabled={loading}
          autoFocus
        >
          {t('common:cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DialogAcceptMaintenanceTask.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  maintenanceTask: PropTypes.object.isRequired,
};

export default DialogAcceptMaintenanceTask;
